//@flow
import React, { PureComponent } from 'react';
import { List, Map } from 'immutable';
import { FixedDataTable } from 'common/components/core/table';

import type { RiderPersonaLimits } from 'common/models/internal/RiderPersonaLimits';
import type { RiderPersonaResourceLimit } from 'common/models/flow/RiderPersonaResourceLimit';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import type { CustomField } from 'common/models/internal/EntitySettings';

import theme from './PersonaLimitsTable.scss';
type Props = {
    limits: ?RiderPersonaLimits,
    unitConverter: UnitConversionHelper,
    t: (string, ?Object) => string,
    tableLabel?: string,
    customResources: List<CustomField>
};

class PersonaLimitsTable extends PureComponent<Props> {
    limitTypes = [
        'task',
        'distance',
        'travelTime',
        'volume',
        'weight',
        'location',
        'totalTime'
    ];
    // This is used to prevent showing unit for a limit type
    hideUnitsForLimit = {
        task: true,
        location: true,
        volume: true
    };

    formRowData = (limit: RiderPersonaResourceLimit, limitType: string) => {
        const { limits, unitConverter, t } = this.props;
        const convertedLowerThreshValue = limits.getPreferredValue({
            value: limit.get('lowerThresh'),
            unitConverter,
            type: limitType
        });
        const convertedHigherThreshValue = limits.getPreferredValue({
            value: limit.get('higherThresh'),
            unitConverter,
            type: limitType
        });
        const unit = this.hideUnitsForLimit[limitType]
            ? ''
            : limits.getPreferredUnitText(unitConverter, limitType);
        const limitMap: Map<string, string> = Map({
            name: t(`riderForm.personaLimits.${limitType}`),
            lowerThresh: `${convertedLowerThreshValue} ${unit}`,
            higherThresh: `${convertedHigherThreshValue} ${unit}`
        });
        return limitMap;
    };

    formCustomResourceRow = (resource: RiderPersonaResourceLimit) => {
        const { customResources } = this.props;
        const customResource = customResources.find(
            customResource => customResource.key === resource.name
        );
        return Map({
            name: customResource?.name || resource.name,
            lowerThresh: resource.lowerThresh,
            higherThresh: resource.higherThresh
        });
    };

    getTableHeaders = () => {
        const { t } = this.props;
        return [
            {
                title: t('riderForm.personaLimits.tableHeaders.limitType'),
                id: 'name'
            },
            {
                title: t('riderForm.personaLimits.tableHeaders.lowerThresh'),
                id: 'lowerThresh'
            },
            {
                title: t('riderForm.personaLimits.tableHeaders.higherThresh'),
                id: 'higherThresh'
            }
        ];
    };

    getTableRows = () => {
        const rows = [];
        const { limits } = this.props;
        const { resourceLimits } = limits;
        this.limitTypes.forEach(limitType => {
            const limit = limits.get(limitType);
            if (limit) {
                rows.push(this.formRowData(limit, limitType));
            }
        });
        //Show custom resource limits from VM/RP
        resourceLimits.forEach(resource => {
            rows.push(this.formCustomResourceRow(resource));
        });
        const rowList: List<any> = List(rows);
        return rowList;
    };

    getTableData = () => {
        return {
            headers: this.getTableHeaders(),
            rows: this.getTableRows()
        };
    };

    render() {
        const { tableLabel, t } = this.props;
        const label = tableLabel || t('riderForm.personaLimits.label');
        return (
            <div className={theme.limitsTable}>
                <span className={theme.label}>{label}</span>
                <FixedDataTable
                    tableData={this.getTableData()}
                    theme={theme}
                    disableScrollbars
                    disableAutoSizer
                />
            </div>
        );
    }
}

export default PersonaLimitsTable;
